/**
 * RelayDataProvider — CC v2 Phase 1 shared data layer.
 *
 * track:track-backlog--cc-v2-phase-1-relaydataprovider--shared-data-layer-no-ui-changes
 * (seq #28585, source finding seq #28573): App.tsx and ControlCenter.tsx
 * each independently subscribe to relay_workspaces, relay_modal_queue, and
 * relay_presence — 2x the necessary Firestore reads for identical data,
 * plus 26 separately tag-filtered team_log_messages listeners across CC +
 * App + BacklogPage. This module is the single source of those reads for
 * CC v2: each hook here is called exactly once, from RelayDataProvider,
 * and every consumer reads the shared result via `useRelayData()`.
 *
 * Phase 1 only builds this layer — nothing in App.tsx or ControlCenter.tsx
 * (v1) is wired to it yet. That migration is Phase 2+.
 *
 * The individual hooks (useWorkspace, useModalQueue, usePresence,
 * useTeamLog, useActiveSprint) are also exported standalone so later
 * migration work and tests can exercise them without the full provider.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "./firebase";
import { subscribeWithReconnect } from "./firestoreReconnect";
import { getCached, setCached, invalidateCached } from "./firestoreCache";
import { getAuthHeader } from "./auth";
import type { TeamLogMessage } from "../types";
import { parseSprintDocId, sprintDocId, sprintKey } from "../../shared/sprintIds";

const WORKSPACE_TTL_MS = 60_000;
const PRESENCE_TTL_MS = 30_000;
const DAEMON_STATUS_TTL_MS = 15_000;
const TEAM_LOG_DEFAULT_LIMIT = 300;

// ---------------------------------------------------------------------------
// Visibility-aware TTL polling — cost-fix (finding #3 from seq #29994).
// useWorkspace/usePresence below poll on a plain setInterval, which used to
// keep firing (and reading Firestore) on its TTL cadence even while the tab
// was backgrounded and nobody was looking at it. This pauses the interval
// on hidden and does an immediate refresh + interval restart on visible,
// mirroring the visibility handling firestoreReconnect.ts already does for
// the real onSnapshot listeners in this file.
// ---------------------------------------------------------------------------

function startVisibilityAwarePolling(load: () => void, intervalMs: number): () => void {
  let interval: ReturnType<typeof setInterval> | null = null;

  function start() {
    if (interval !== null) return;
    interval = setInterval(load, intervalMs);
  }

  function stop() {
    if (interval !== null) {
      clearInterval(interval);
      interval = null;
    }
  }

  function onVisibilityChange() {
    if (document.visibilityState === "visible") {
      load();
      start();
    } else {
      stop();
    }
  }

  if (typeof document === "undefined") {
    start();
    return stop;
  }

  document.addEventListener("visibilitychange", onVisibilityChange);
  if (document.visibilityState === "visible") start();

  return () => {
    stop();
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}

// ---------------------------------------------------------------------------
// relay_workspaces — TTL-cached, replaces duplicate listeners at
// App.tsx:967 and ControlCenter.tsx:3337.
// ---------------------------------------------------------------------------

export interface WorkspaceData {
  pinned_filters: string[];
  projects: unknown[];
  active_project_slug: string | null;
  hidden_projects: string[];
  // cost-audit fix (seq #29785) — tier/relay-mode fields, added so
  // useWorkspaceTier/useRelayModeSettings can read from this shared
  // cached doc instead of each opening its own onSnapshot listener on
  // the same relay_workspaces/{id}.
  //
  // track:workspace-write-auth (#48220 Track A, Outcome 4) — `tier` is
  // billing-only as of this track (free/founder); it is no longer the
  // Agent Autonomy setting. `agent_autonomy_tier` is the new, separate
  // field for that — see useAgentAutonomyTier.ts.
  tier: string | null;
  agent_autonomy_tier: string | null;
  relay_narration_mode: string | null;
  // track:track-backlog--validation-step-1-revised-workspace-toggle--pipeline-stage
  // (ported for cc-v2-gap-remediation Task 3) — workspace_rule_overrides['validation-enabled'],
  // read off the same relay_workspaces doc as the other fields above. Defaults OFF
  // (manual PR review) when unset, matching ControlCenter.tsx v1's documented default.
  validation_enabled: boolean;
}

function emptyWorkspace(): WorkspaceData {
  return {
    pinned_filters: [],
    projects: [],
    active_project_slug: null,
    hidden_projects: [],
    tier: null,
    agent_autonomy_tier: null,
    relay_narration_mode: null,
    validation_enabled: false,
  };
}

function parseWorkspace(data: Record<string, unknown> | undefined): WorkspaceData {
  if (!data) return emptyWorkspace();
  return {
    pinned_filters: Array.isArray(data.pinned_filters) ? (data.pinned_filters as string[]) : [],
    projects: Array.isArray(data.projects) ? (data.projects as unknown[]) : [],
    active_project_slug: typeof data.active_project_slug === "string" ? data.active_project_slug : null,
    hidden_projects: Array.isArray(data.hidden_projects) ? (data.hidden_projects as string[]) : [],
    tier: typeof data.tier === "string" ? data.tier : null,
    agent_autonomy_tier: typeof data.agent_autonomy_tier === "string" ? data.agent_autonomy_tier : null,
    relay_narration_mode: typeof data.relay_narration_mode === "string" ? data.relay_narration_mode : null,
    validation_enabled: (data.workspace_rule_overrides as Record<string, unknown> | undefined)?.["validation-enabled"] === true,
  };
}

export function useWorkspace(workspaceId: string): { data: WorkspaceData; loading: boolean; refetch: () => Promise<void> } {
  const cacheKey = `relay_workspaces:${workspaceId}`;
  const cached = getCached<WorkspaceData>(cacheKey);
  const [data, setData] = useState<WorkspaceData>(cached ?? emptyWorkspace());
  const [loading, setLoading] = useState(cached === undefined);
  const mountedRef = useRef(true);

  // `load` is a stable callback (not an effect-local closure) so both the
  // mount/poll effect below and the externally-exposed `refetch` can share
  // it — see `refetch`'s own comment for why a way to force this outside
  // the 60s poll cadence is needed at all.
  const load = useCallback(async () => {
    if (!workspaceId) {
      if (mountedRef.current) {
        setData(emptyWorkspace());
        setLoading(false);
      }
      return;
    }
    const fresh = getCached<WorkspaceData>(cacheKey);
    if (fresh !== undefined) {
      if (mountedRef.current) {
        setData(fresh);
        setLoading(false);
      }
      return;
    }
    try {
      const snap = await getDoc(doc(db, "relay_workspaces", workspaceId));
      if (!mountedRef.current) return;
      const next = snap.exists() ? parseWorkspace(snap.data()) : emptyWorkspace();
      setCached(cacheKey, next, WORKSPACE_TTL_MS);
      setData(next);
    } catch (err) {
      console.warn("[RelayDataProvider] workspace read failed:", err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [workspaceId, cacheKey]);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    const stopPolling = startVisibilityAwarePolling(() => void load(), WORKSPACE_TTL_MS);
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [load]);

  // track:track-backlog--workspace-governance-settings-appear-broken-stale-cache
  // — useWorkspaceTier/useRelayModeSettings both derive
  // from this cached read (cost-audit fix, seq #29785, which replaced their
  // own live onSnapshot listeners with this shared TTL cache). Nothing ever
  // invalidated that cache after a write, so WorkspaceGovernanceSection's
  // settings appeared to silently do nothing for up to WORKSPACE_TTL_MS
  // after every change — the write itself always succeeded. `refetch`
  // clears this hook's cache entry and immediately re-runs `load`, so a
  // caller can get an instant, correct read right after its own write
  // instead of waiting out the poll cadence.
  const refetch = useCallback(async () => {
    invalidateCached(cacheKey);
    await load();
  }, [cacheKey, load]);

  return { data, loading, refetch };
}

// ---------------------------------------------------------------------------
// relay_presence/{workspaceId}/agents — TTL-cached, replaces duplicate
// listeners at App.tsx's useAttentionCount presence effect and
// ControlCenter.tsx:3318.
// ---------------------------------------------------------------------------

export function usePresence(workspaceId: string): { agents: Map<string, number>; states: Map<string, string>; loading: boolean } {
  const cacheKey = `relay_presence:${workspaceId}`;
  const statesKey = `relay_presence_states:${workspaceId}`;
  const cached = getCached<Map<string, number>>(cacheKey);
  const [agents, setAgents] = useState<Map<string, number>>(cached ?? new Map());
  const [states, setStates] = useState<Map<string, string>>(getCached<Map<string, string>>(statesKey) ?? new Map());
  const [loading, setLoading] = useState(cached === undefined);

  useEffect(() => {
    if (!workspaceId) {
      setAgents(new Map());
      setStates(new Map());
      setLoading(false);
      return;
    }
    let cancelled = false;

    async function load() {
      const fresh = getCached<Map<string, number>>(cacheKey);
      if (fresh !== undefined) {
        setAgents(fresh);
        setStates(getCached<Map<string, string>>(statesKey) ?? new Map());
        setLoading(false);
        return;
      }
      try {
        const snap = await getDocs(collection(db, "relay_presence", workspaceId, "agents"));
        if (cancelled) return;
        const next = new Map<string, number>();
        const nextStates = new Map<string, string>();
        for (const d of snap.docs) {
          const lastSeen = d.data().last_seen;
          const ts = lastSeen ? new Date(lastSeen).getTime() : NaN;
          if (!Number.isNaN(ts)) next.set(d.id, ts);
          const state = d.data().state;
          if (typeof state === "string") nextStates.set(d.id, state);
        }
        setCached(statesKey, nextStates, PRESENCE_TTL_MS);
        setCached(cacheKey, next, PRESENCE_TTL_MS);
        setAgents(next);
        setStates(nextStates);
      } catch (err) {
        console.warn("[RelayDataProvider] presence read failed:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    const stopPolling = startVisibilityAwarePolling(() => void load(), PRESENCE_TTL_MS);
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [workspaceId, cacheKey, statesKey]);

  return { agents, states, loading };
}

// ---------------------------------------------------------------------------
// relay_modal_queue — real-time (drives the action chime), replaces
// duplicate listeners at App.tsx:929 and ControlCenter.tsx:3450.
// ---------------------------------------------------------------------------

export interface ModalQueueAction {
  label: string;
  message: string;
  tags?: string[];
  variant?: string;
}

export interface ModalQueueItem {
  id: string;
  author: string;
  blast?: string;
  // "alert" — adk-relay's fire_blocker_modal (kind="alert") — carries real
  // actions (see below), not a preflight/confirm/credential decision.
  type?: "confirm" | "preflight" | "peerReview" | "credential" | "alert";
  title: string;
  description?: string;
  files?: string[];
  state: string;
  createdAt?: unknown;
  // Mirrored from the source relay_modal/default trigger's `actions` array
  // (backlog #35460-followup) — when present, this is the item's real
  // resolution: each action's `message`/`tags` is what must be posted to
  // the Team Log to actually resolve whatever this item represents. Absent
  // for items with no source actions (e.g. POST /api/modal/queue's
  // confidence-escalation items), which still use the generic Approve/
  // Reject state-flip.
  actions?: ModalQueueAction[] | null;
  // Who produced the alert, written by the producer on the trigger doc. Only
  // "scheduler" and "daemon" are counted by the Actions tab.
  source?: string | null;
}

// Maps a relay_modal/default trigger doc to the queue item's fields (state and
// createdAt are added by the writer). Pure so the defaults are testable.
export function queueItemFromModalTrigger(data: Record<string, unknown>) {
  return {
    author: (data.author as string) ?? "relay",
    title: data.title as string,
    description: (data.content as string) ?? (data.description as string) ?? "",
    blast: (data.blast as string) ?? "MEDIUM",
    type: (data.kind as string) ?? "confirm",
    files: Array.isArray(data.files) ? data.files : [],
    // The trigger's `actions` array (backlog #35460-followup): each item's
    // real resolution — label + Team Log message + tags.
    actions: Array.isArray(data.actions) ? data.actions : null,
    source: typeof data.source === "string" && data.source ? data.source : null,
  };
}

export function useModalQueue(workspaceId: string): { pendingCount: number; items: ModalQueueItem[] } {
  const [items, setItems] = useState<ModalQueueItem[]>([]);

  useEffect(() => {
    if (!workspaceId) {
      setItems([]);
      return;
    }
    const q = query(
      collection(db, "relay_modal_queue", workspaceId, "items"),
      where("state", "==", "pending"),
      orderBy("createdAt", "desc"),
      fsLimit(50),
    );
    return subscribeWithReconnect(({ retry, markConnected }) =>
      onSnapshot(
        q,
        snap => {
          markConnected();
          setItems(snap.docs.map(d => ({ id: d.id, ...d.data() }) as ModalQueueItem));
        },
        err => {
          console.warn("[RelayDataProvider] modal queue", err);
          retry(err);
        },
      ),
    );
  }, [workspaceId]);

  return { pendingCount: items.length, items };
}

// ---------------------------------------------------------------------------
// team_log_messages — one real-time subscription; consumers filter by tag
// client-side instead of each opening its own tag-filtered listener.
// Replaces the 26 tag-filtered listeners across CC + App + BacklogPage.
// ---------------------------------------------------------------------------

export interface UseTeamLogOptions {
  limit?: number;
}

export function useTeamLog(
  workspaceId: string,
  options: UseTeamLogOptions = {},
): { messages: TeamLogMessage[]; loading: boolean } {
  const limitN = options.limit ?? TEAM_LOG_DEFAULT_LIMIT;
  const [messages, setMessages] = useState<TeamLogMessage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspaceId) {
      setMessages([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, "team_log_messages"),
      where("workspace_id", "==", workspaceId),
      orderBy("sequence", "desc"),
      fsLimit(limitN),
    );
    return subscribeWithReconnect(({ retry, markConnected }) =>
      onSnapshot(
        q,
        snap => {
          markConnected();
          setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() }) as TeamLogMessage));
          setLoading(false);
        },
        err => {
          console.warn("[RelayDataProvider] team log", err);
          retry(err);
        },
      ),
    );
  }, [workspaceId, limitN]);

  return { messages, loading };
}

/** Client-side tag filter helper for consumers of useTeamLog's shared feed. */
export function selectByTag(messages: TeamLogMessage[], tag: string): TeamLogMessage[] {
  return messages.filter(m => (m.tags ?? []).includes(tag));
}

// ---------------------------------------------------------------------------
// Tag-scoped team_log_messages — cost-audit fix (seq #29787, #29861).
// useTeamLog's generic top-N window re-delivers its full page on every
// single Team Log write workspace-wide, and shrinking N isn't safe on its
// own: ActionsTab's blocked/pre-flight/requires-response items are
// open-ended (not sprint-scoped), so a smaller generic window can silently
// drop a genuinely still-open item once enough unrelated chatter pushes it
// out. A query scoped to the specific tags each consumer actually needs
// re-fires only on writes matching those tags — far less often than "every
// write" — and, being tag-scoped rather than position-scoped, doesn't lose
// an old-but-still-open item just because other messages piled up since.
// ---------------------------------------------------------------------------

const TAGGED_DEFAULT_LIMIT = 200;

export function useTaggedTeamLog(
  workspaceId: string,
  tags: string[],
  options: UseTeamLogOptions = {},
): { messages: TeamLogMessage[]; loading: boolean } {
  const limitN = options.limit ?? TAGGED_DEFAULT_LIMIT;
  const tagsKey = tags.join(",");
  const [messages, setMessages] = useState<TeamLogMessage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspaceId || tags.length === 0) {
      setMessages([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, "team_log_messages"),
      where("workspace_id", "==", workspaceId),
      where("tags", "array-contains-any", tags),
      orderBy("sequence", "desc"),
      fsLimit(limitN),
    );
    return subscribeWithReconnect(({ retry, markConnected }) =>
      onSnapshot(
        q,
        snap => {
          markConnected();
          setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() }) as TeamLogMessage));
          setLoading(false);
        },
        err => {
          console.warn("[RelayDataProvider] tagged team log", err);
          retry(err);
        },
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tagsKey stands in for tags (array identity)
  }, [workspaceId, tagsKey, limitN]);

  return { messages, loading };
}

// ---------------------------------------------------------------------------
// Active sprint — pointer comes straight from relay_sprints (status active/
// pending, highest sprint number wins), then the relay_sprints/{id} items
// array. track:track-sprint-61 already established this same pattern
// server-side for the scheduler; this used to derive the pointer from
// team_log_messages sprint-planned/sprint-closed tags, which depended on a
// client-authored Team Log post landing — see the em-dash incident writeup
// (Team Log seq #30556/#30572) for why that was fragile.
// ---------------------------------------------------------------------------

export interface ActiveSprintInfo {
  sprintId: string | null;
  items: unknown[];
  /** Every open (active/pending) sprint id, so the board can warn when more than one is open. */
  openSprintIds?: string[];
}

export function useActiveSprint(workspaceId: string): ActiveSprintInfo {
  const [sprintId, setSprintId] = useState<string | null>(null);
  const [items, setItems] = useState<unknown[]>([]);
  const [openSprintIds, setOpenSprintIds] = useState<string[]>([]);

  useEffect(() => {
    if (!workspaceId) {
      setSprintId(null);
      setOpenSprintIds([]);
      return;
    }
    // track:sprint-board-workspace-scope — this query had no workspace_id
    // filter at all, so it picked the highest-numbered active/pending
    // sprint doc across every workspace, not just this one. Safe to scope
    // strictly now: confirmed live (230/231 relay_sprints docs carry
    // workspace_id, the lone exception is status:held so it's already
    // excluded by the status filter below) and matches the strict-equality
    // pattern server/modules/team/restApi.ts's sprint/items routes already
    // use (track:track-workspace-id-migration — absence is treated as a
    // mismatch, not failed open).
    const q = query(
      collection(db, "relay_sprints"),
      where("workspace_id", "==", workspaceId),
      where("status", "in", ["active", "pending"]),
    );
    return subscribeWithReconnect(({ retry, markConnected }) =>
      onSnapshot(
        q,
        snap => {
          markConnected();
          const open = snap.docs
            .map(d => parseSprintDocId(d.id))
            .filter((p): p is { workspaceId: string; n: number } => p !== null && p.workspaceId === workspaceId)
            .map(p => p.n);
          setSprintId(open.length ? sprintKey(Math.max(...open)) : null);
          setOpenSprintIds([...open].sort((a, b) => a - b).map(n => sprintKey(n)));
        },
        err => {
          console.warn("[RelayDataProvider] active sprint pointer", err);
          retry(err);
        },
      ),
    );
  }, [workspaceId]);

  useEffect(() => {
    if (!sprintId) {
      setItems([]);
      return;
    }
    return subscribeWithReconnect(({ retry, markConnected }) =>
      onSnapshot(
        doc(db, "relay_sprints", sprintDocId(workspaceId, sprintId.replace(/^sprint-/, ""))),
        snap => {
          markConnected();
          setItems(snap.exists() ? ((snap.data()?.items as unknown[]) ?? []) : []);
        },
        err => {
          console.warn("[RelayDataProvider] active sprint items", err);
          retry(err);
        },
      ),
    );
  }, [workspaceId, sprintId]);

  return { sprintId, items, openSprintIds };
}

// ---------------------------------------------------------------------------
// Daemon status — HTTP poll against /api/daemon/status, not Firestore-backed.
// Mirrors ControlCenter.tsx's checkDaemonStatus (track:track-fix-daemon-online-check)
// — same endpoint, same "online === true" check, same 429-means-offline
// handling — but polls via startVisibilityAwarePolling instead of a plain
// setInterval so it pauses while the tab is backgrounded, consistent with
// every other poll in this file.
// ---------------------------------------------------------------------------

export function useDaemonStatus(): { online: boolean } {
  const [online, setOnline] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const headers = await getAuthHeader();
        const res = await fetch("/api/daemon/status", { headers });
        if (cancelled) return;
        if (res.status === 429) {
          setOnline(false);
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setOnline(data.online === true);
      } catch (err) {
        if (cancelled) return;
        console.warn("[RelayDataProvider] daemon status poll failed:", err);
        setOnline(false);
      }
    }

    void load();
    const stopPolling = startVisibilityAwarePolling(() => void load(), DAEMON_STATUS_TTL_MS);
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, []);

  return { online };
}

// ---------------------------------------------------------------------------
// Context — one instance of each hook above, shared to every consumer.
// ---------------------------------------------------------------------------

export interface RelayData {
  workspace: WorkspaceData;
  workspaceLoading: boolean;
  presence: Map<string, number>;
  /** relay_presence state per agent ("busy", "online"…). Optional so older callers and test mocks still type-check. */
  presenceStates?: Map<string, string>;
  presenceLoading: boolean;
  modalQueuePendingCount: number;
  modalQueueItems: ModalQueueItem[];
  activeSprint: ActiveSprintInfo;
  daemonOnline: boolean;
}

const RelayDataContext = createContext<RelayData | null>(null);

export function RelayDataProvider({
  workspaceId,
  children,
}: {
  workspaceId: string;
  children: ReactNode;
}) {
  const { data: workspace, loading: workspaceLoading } = useWorkspace(workspaceId);
  const { agents: presence, states: presenceStates, loading: presenceLoading } = usePresence(workspaceId);
  const { pendingCount: modalQueuePendingCount, items: modalQueueItems } = useModalQueue(workspaceId);
  const activeSprint = useActiveSprint(workspaceId);
  const { online: daemonOnline } = useDaemonStatus();

  const value = useMemo<RelayData>(
    () => ({
      workspace,
      workspaceLoading,
      presence,
      presenceStates,
      presenceLoading,
      modalQueuePendingCount,
      modalQueueItems,
      activeSprint,
      daemonOnline,
    }),
    [workspace, workspaceLoading, presence, presenceStates, presenceLoading, modalQueuePendingCount, modalQueueItems, activeSprint, daemonOnline],
  );

  return <RelayDataContext.Provider value={value}>{children}</RelayDataContext.Provider>;
}

/** Like useRelayData, but null outside a provider — for shared components that also render outside the Control Center. */
export function useOptionalRelayData(): RelayData | null {
  return useContext(RelayDataContext);
}

export function useRelayData(): RelayData {
  const ctx = useContext(RelayDataContext);
  if (!ctx) throw new Error("useRelayData must be used within a RelayDataProvider");
  return ctx;
}
